import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import moment from 'moment';
import './eventTitleAndReview.less';
import useGetGrossPrice from '../../../../hooks/useGetGrossPrice';
import { currencySignsObj } from '@skygroup/shared/utils/constants';
import { EnvironmentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip, Tag } from 'antd';
import MapWrapper from '../../../../components/Map/MapWrapper';
import { isMapsEnabled } from '../../../../utils/featureFlags';
import { getDateNotFinalTooltipText } from '../../utils/eventDateTimeHelpers';
import SelectedEventBanner from '../../../SwitchDatePicker/components/SearchResults/SearchResultsDynamicPackages/SelectedEventBanner/SelectedEventBanner';

interface EventTitleAndReviewProps {
  event: any;
  agentCommissionInfo?: {
    suggested_commission: number;
    supplier_name: string;
    ts_type: string;
  };
  isPackageView?: boolean;
  ticket?: any;
}

const EventTitleAndReview = ({
  event,
  agentCommissionInfo,
  isPackageView = false,
  ticket,
}: EventTitleAndReviewProps) => {
  const getPrice = useGetGrossPrice();
  const { general: t_general, sportsAndEvents: t_sportsAndEvents } =
    useSelector((data: RootState) => data.config.translations);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const formatEventDate = () => {
    // New format from mock data: dateOfEvent and timeOfEvent
    if (event?.dateOfEvent) {
      const dateStr = event.dateOfEvent;
      const timeStr = event?.timeOfEvent;
      const atSep = t_sportsAndEvents?.dateTimeSeparator ?? ' at ';
      return `${dateStr}${timeStr ? `${atSep}${timeStr}` : ''}`;
    }
    // Fallback to old format
    if (event?.startDate) {
      const startDate = moment(event.startDate);
      if (event?.endDate) {
        const endDate = moment(event.endDate);
        if (startDate.isSame(endDate, 'day')) {
          return startDate.format('MMMM DD, YYYY');
        }
        return `${startDate.format('MMMM DD')} - ${endDate.format(
          'MMMM DD, YYYY'
        )}`;
      }
      return startDate.format('MMMM DD, YYYY');
    }
    return '';
  };

  const renderParticipantItem = (
    participant: {
      id: string | number;
      name: string;
      logo?: string;
      description?: string;
    },
    index: number
  ) => {
    const tooltipContent = participant.description ? (
      <div
        dangerouslySetInnerHTML={{ __html: participant.description }}
        className="participant-description-tooltip"
      />
    ) : null;

    return (
      <>
        {index > 0 && <span key={`${participant.id}-separator`}> - </span>}
        <Tooltip
          key={participant.id}
          title={tooltipContent}
          overlayClassName="participant-tooltip"
        >
          <div className="participant-item">
            {participant.logo && (
              <img src={participant.logo} alt={participant.name} />
            )}
            <span>{participant.name}</span>
          </div>
        </Tooltip>
      </>
    );
  };

  const renderParticipantsList = () => {
    if (!event?.participants || event.participants.length === 0) {
      return null;
    }

    if (event.participants.every((p: {id: string, name: string}) => !p.id || !p.name)) {
      return null;
    }

    return (
      <div className="participants-logos">
        {event.participants.map(renderParticipantItem)}
      </div>
    );
  };

  const renderTeamMatchup = () => {
    const { homeTeam, awayTeam } = event || {};
    let text = '';

    if (homeTeam?.id || homeTeam?.name) {
      text += homeTeam.name;
    }

    if (awayTeam?.id || awayTeam?.name) {

      if (text?.length > 0) {
        text += " - ";
      }

      text += awayTeam.name;
    }

    return text;
  };

  const getParticipantsDisplay = () => {
    const participantsList = renderParticipantsList();
    const teamMatchup = renderTeamMatchup();
    const participantsContent = participantsList || teamMatchup;

    if (!participantsContent) {
      return null;
    }

    return (
      <>
        {t_sportsAndEvents?.participants}: {participantsContent}
      </>
    );
  };

  const getLocationString = () => {
    const parts = [];
    if (event?.city?.name) parts.push(event.city.name);
    if (event?.country?.name) parts.push(event.country.name);
    if (event?.venue?.name) parts.push(event.venue.name);
    return parts.join(', ');
  };

  // maps off (REACT_APP_MAPS_ENABLED) - the location is plain text, not a map link
  const venueMapData =
    isMapsEnabled() && event?.venue?.latitude && event?.venue?.longitude
      ? [
          {
            position: [event.venue.latitude, event.venue.longitude],
            name: event.venue.name || t_sportsAndEvents?.venue,
            address: event.venue.address || '',
            id: event.venue.id?.toString() || '1',
          },
        ]
      : [];

  const handleMapClick = () => {
    if (venueMapData.length > 0) {
      setMapModalOpen(true);
    }
  };

  const dateAndTimeNotFinal =
    event?.finalDate === false && event?.finalTime === false;
  const onlyTimeNotFinal =
    event?.finalDate === true && event?.finalTime === false;
  const onlyDateNotFinal =
    event?.finalDate === false && event?.finalTime === true;
  const dateOrTimeNotFinal =
    event?.finalDate === false || event?.finalTime === false;
  const renderDateNotFinalTooltip = () =>
    getDateNotFinalTooltipText(event, {
      dateAndTimeAreSubjectToChange:
        t_sportsAndEvents.dateAndTimeAreSubjectToChange,
      dateIsSubjectToChange: t_sportsAndEvents.dateIsSubjectToChange,
      timeIsSubjectToChange: t_sportsAndEvents.timeIsSubjectToChange,
    });
  const renderDateNotFinalLabel = () => {
    if (dateAndTimeNotFinal) {
      return t_sportsAndEvents.dateAndTimeNotFinal;
    } else if (onlyDateNotFinal) {
      return t_sportsAndEvents.dateNotFinal;
    } else if (onlyTimeNotFinal) {
      return t_sportsAndEvents.timeNotFinal;
    }
  };

  const renderParticipants = () =>
    (event?.homeTeam || event?.participants?.length > 0) && (
      <div className="participants-display">{getParticipantsDisplay()}</div>
    );

  const renderPrice = () => {
    if (!event?.minTicketPrice?.price) {
      return null;
    }
    return (
      <div className="price-wrapper">
        <span className="price-per-person">
          <span>{`${t_general.from} `}</span>
          <span className="price">
            {`${
              currencySignsObj?.[
                event?.minTicketPrice?.currency?.toLowerCase() as keyof typeof currencySignsObj
              ] ?? event?.minTicketPrice?.currency
            } ${getPrice(
              event?.minTicketPrice?.price,
              agentCommissionInfo?.suggested_commission,
              0
            )}`}
          </span>
        </span>
        <div className={'disclaimer'}>{t_sportsAndEvents?.pricePerTicket}</div>
      </div>
    );
  };

  if (!event) {
    return null;
  }

  // Event view: the hero carries the whole header. It already renders the
  // title, type/tournament chips, date and location (with its own venue map),
  // so only participants and price are slotted in. No currentStep, so no
  // events-DP stepper strip or back button.
  if (!isPackageView) {
    return (
      <span className="event-title-review-wrapper">
        <SelectedEventBanner
          event={event}
          ticket={ticket}
          className="event-page-hero"
          aside={renderPrice()}
          extra={renderParticipants()}
        />
      </span>
    );
  }

  return (
    <span className="event-title-review-wrapper">
      <div className="event-header-container">
        <div className="event-page-title">
          <span className="description">{event?.name || event?.title}</span>

          {/* Location */}
          {getLocationString() && (
            <div
              className={`location${
                venueMapData.length > 0 ? ' clickable-venue-group' : ''
              }`}
              onClick={handleMapClick}
            >
              <EnvironmentOutlined />
              <span>{getLocationString()}</span>
            </div>
          )}

          {/* Enhanced Header Information */}
          <div className="event-meta-info">
            {event?.eventType?.name && (
              <Tag color="blue" className="event-meta-tag">
                {/* {t_sportsAndEvents?.eventType}: {event.eventType.name} */}
                {event.eventType.name}
              </Tag>
            )}
            {event?.tournament?.name && (
              <Tag color="purple" className="event-meta-tag">
                {/* {t_sportsAndEvents?.tournament}: {event.tournament.name} */}
                {event.tournament.logo && (
                  <img
                    src={event.tournament.logo}
                    alt={event.tournament.name}
                    className="tournament-logo"
                    width={24}
                    height={24}
                  />
                )}
                {event.tournament.name}
              </Tag>
            )}

            {formatEventDate() && (
              <Tag
                color={dateOrTimeNotFinal ? 'orange' : 'green'}
                className="event-meta-tag"
              >
                {formatEventDate()}
                {/* Date Not Final Hint */}
                {dateOrTimeNotFinal && (
                  <Tooltip
                    className="date not-final-tooltip"
                    title={renderDateNotFinalTooltip()}
                  >
                    <InfoCircleOutlined />
                    <span>{renderDateNotFinalLabel()}</span>
                  </Tooltip>
                )}
              </Tag>
            )}
          </div>

          {renderParticipants()}
        </div>
      </div>

      {/* Map Modal */}
      {venueMapData.length > 0 && (
        <MapWrapper
          data={[
            {
              position: [event.venue.latitude, event.venue.longitude],
              price: 0,
              currency: '',
            },
          ]}
          title={event.venue?.name || t_sportsAndEvents?.venue}
          initialZoom={16}
          previewInitZoom={14}
          preview_type="small"
          isOpen={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
        />
      )}
    </span>
  );
};

export default EventTitleAndReview;
